import React, { useEffect, useState } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { EllipsisMenu } from "@features/shared/components/ellipsis-menu";
import { useFeatureFlags, useUpdateUserFeatureFlag } from "@hooks/http";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

export function TableViewSelector() {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { featureFlags, loading: featureFlagDataLoading } = useFeatureFlags();

  const [updateFeatureFlag, { loading: updateFeatureFlagLoading }] =
    useUpdateUserFeatureFlag();
  const isLoading = featureFlagDataLoading || updateFeatureFlagLoading;
  const isCompacted = Boolean(
    featureFlags?.["salesPageV3UsingCompactTableHeight"],
  );
  const isInfiniteScroll = Boolean(
    featureFlags?.["salesPageV3UsingPaginatedTableExperience"],
  );

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [proposedCompactRowHeight, setProposedCompactRowHeight] =
    useState(isCompacted);
  const [proposedUsingInfiniteScroll, setPropsedUsingInfiniteScroll] =
    useState(isInfiniteScroll);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    setProposedCompactRowHeight(isCompacted);
  }, [isCompacted]);

  useEffect(() => {
    setPropsedUsingInfiniteScroll(isInfiniteScroll);
  }, [isInfiniteScroll]);

  const onClose = () => setModalIsOpen(false);

  return (
    <>
      <EllipsisMenu>
        <EllipsisMenu.Item as="button" onClick={() => setModalIsOpen(true)}>
          Table appearance
        </EllipsisMenu.Item>
      </EllipsisMenu>
      <Modal
        isOpen={modalIsOpen}
        onClose={onClose}
        titleContent={<div className="text-xl font-bold">Table appearance</div>}
      >
        <div className="mb-6">
          <div className="text-sm">ROW HEIGHT</div>
          <div className="flex items-center gap-2 mt-2">
            <div
              className={clsx(
                "w-8 h-8 flex items-center justify-center border rounded-md cursor-pointer",
                {
                  "bg-cosmic": !proposedCompactRowHeight,
                  "text-white": !proposedCompactRowHeight,
                },
              )}
              onClick={() => {
                prettyLogEventPage({
                  domain: "Table View Selector",
                  action: "onClick",
                  additionalActionInfo: "Set Full Row Height",
                });

                setProposedCompactRowHeight(false);
                setHasUnsavedChanges(true);
              }}
            >
              <i className="fa-sharp fa-regular fa-grid-2 text-sm"></i>
            </div>
            <div
              className={clsx(
                "w-8 h-8 flex items-center justify-center border rounded-md cursor-pointer",
                {
                  "bg-cosmic": proposedCompactRowHeight,
                  "text-white": proposedCompactRowHeight,
                },
              )}
              onClick={() => {
                prettyLogEventPage({
                  domain: "Table View Selector",
                  action: "onClick",
                  additionalActionInfo: "Set Compact Row Height",
                });

                setProposedCompactRowHeight(true);
                setHasUnsavedChanges(true);
              }}
            >
              <i className="fa-sharp fa-regular fa-grid text-sm"></i>
            </div>
          </div>
        </div>
        <div>
          <div className="text-sm">PAGINATION TYPE</div>
          <div className="flex items-center gap-2 mt-2">
            <PrimaryButton
              variant={proposedUsingInfiniteScroll ? "primary" : "secondary"}
              onClick={() => {
                prettyLogEventPage({
                  domain: "Table View Selector",
                  action: "onClick",
                  additionalActionInfo: "Set Infinite Scroll",
                });

                setPropsedUsingInfiniteScroll(true);
                setHasUnsavedChanges(true);
              }}
            >
              Infinite Scroll
            </PrimaryButton>
            <PrimaryButton
              variant={!proposedUsingInfiniteScroll ? "primary" : "secondary"}
              onClick={() => {
                prettyLogEventPage({
                  domain: "Table View Selector",
                  action: "onClick",
                  additionalActionInfo: "Set Traditional Scroll",
                });

                setPropsedUsingInfiniteScroll(false);
                setHasUnsavedChanges(true);
              }}
            >
              Traditional Scroll
            </PrimaryButton>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <TertiaryButton
            loading={isLoading}
            onClick={onClose}
            className="mr-2"
          >
            <div className="flex flex-row items-center">Cancel</div>
          </TertiaryButton>
          <PrimaryButton
            isDisabled={!hasUnsavedChanges}
            loading={isLoading}
            onClick={async () => {
              prettyLogEventPage({
                domain: "Table View Selector",
                action: "onClick",
                additionalActionInfo: "Commit Changes",
              });

              await updateFeatureFlag(
                "salesPageV3UsingCompactTableHeight",
                proposedCompactRowHeight,
                false,
              );
              await updateFeatureFlag(
                "salesPageV3UsingPaginatedTableExperience",
                proposedUsingInfiniteScroll,
                true,
              );
              setHasUnsavedChanges(false);
              onClose();
            }}
          >
            Save
          </PrimaryButton>
        </div>
      </Modal>
    </>
  );
}
